import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PageTitle } from '../CoachDashboard/styles';
import { athleteAPI, stripeAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { PLAN_HIERARCHY, isUpgrade, isDowngrade } from '../../utils/subscriptionUtils';
import PlanBadge from '../Common/PlanBadge';

const MIN_AGE = 16;

function getAgeFromDateOfBirth(dateOfBirth: string): number | null {
  if (!dateOfBirth || !dateOfBirth.trim()) return null;
  const d = new Date(dateOfBirth.trim());
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

const CopyIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const PageSubtitle = styled.div`
  font-size: 1rem;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 1.5rem;
  text-align: left;
  max-width: 700px;
  color: var(--text-gray);
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid var(--card-border);
  width: 100%;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 1rem 2rem;
  border: none;
  background: transparent;
  color: ${props => props.active ? 'var(--accent-orange)' : 'var(--text-gray)'};
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  border-bottom: 3px solid ${props => props.active ? 'var(--accent-orange)' : 'transparent'};
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: -0.05em;
  
  &:hover {
    color: var(--accent-orange);
  }
`;

const SettingsSection = styled.div`
  width: 100%;
  max-width: 900px;
  margin-bottom: 1.5rem;
`;

const ProfileSectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  width: 100%;
`;

const ProfileHeaderActions = styled.div`
  margin-left: auto;
`;

const ProfileCard = styled.div`
  flex: 1;
  min-width: 180px;
  padding: 1.5rem;
  margin: 0;
  background: var(--bg-sidebar);
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
`;

const SettingsCard = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

/* Row of 3 segment cards side by side */
const ProfileSegmentsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  width: 100%;
`;

const ProfileCardTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 800;
  color: var(--accent-orange);
  margin-bottom: 0.75rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

/* Grid of info items inside each segment — no frames, no extra background */
const ProfileCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
`;

const ProfileInfoTile = styled.div`
  padding: 0.25rem 0;
  min-height: 0;
`;

const ProfileInfoTileLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.2rem;
  font-family: 'Poppins', sans-serif;
`;

const ProfileInfoTileValue = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  word-break: break-word;
  line-height: 1.3;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--card-border);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const InfoValue = styled.div`
  font-size: 0.95rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const EditProfileActions = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  @media (max-width: 768px) {
    margin-top: 1.75rem;
    justify-content: center;
  }
`;

const DeleteDataArea = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: rgba(255, 100, 100, 0.06);
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 12px;
  width: 100%;
`;

const DeleteDataTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-gray);
  margin-bottom: 0.75rem;
`;

const DeleteDataCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  input { accent-color: var(--accent-orange); cursor: pointer; }
`;

const DeleteDataWarning = styled.div`
  font-size: 0.85rem;
  color: #c62828;
  margin-top: 0.5rem;
  font-weight: 600;
`;

const Label = styled.label`
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-gray);
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--card-border);
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  background: var(--bg-primary);
  color: var(--text-gray);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
  }

  &:disabled {
    background: var(--bg-tertiary);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--card-border);
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  background: var(--bg-primary);
  color: var(--text-gray);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
  }

  &:disabled {
    background: var(--bg-tertiary);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Button = styled.button`
  padding: var(--button-padding, 0.4rem 0.75rem);
  border: none;
  border-radius: 8px;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    font-weight: 800;
    min-height: 2.5rem;
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--accent-orange);
  color: #fff;
  border: 2px solid var(--accent-orange);

  &:hover:not(:disabled) {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    font-weight: 800;
    min-height: 2.5rem;
  }
`;

const DangerButton = styled(Button)`
  background: linear-gradient(135deg, #FF3333 0%, #CC0000 100%);
  color: #fff;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #CC0000 0%, #990000 100%);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: var(--accent-orange);
  border: 2px solid var(--accent-orange);

  &:hover:not(:disabled) {
    background: var(--accent-orange);
    color: #fff;
  }
`;

const DeleteConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const DeleteConfirmModal = styled.div`
  background: var(--bg-primary);
  border-radius: 16px;
  max-width: 440px;
  width: 100%;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  font-family: 'Poppins', sans-serif;
`;

const DeleteConfirmTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.2rem;
  color: var(--text-gray);
  font-weight: 800;
`;

const DeleteConfirmText = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.95rem;
  color: var(--text-gray);
  line-height: 1.5;
`;

const DeleteConfirmWarning = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.75rem 1rem;
  background: rgba(198, 40, 40, 0.12);
  border: 1px solid rgba(198, 40, 40, 0.4);
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #b71c1c;
  font-family: 'Poppins', sans-serif;
  line-height: 1.4;
`;

const DeleteConfirmButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const InfoText = styled.div`
  font-size: 0.9rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
  font-style: italic;
`;

const InlineFieldError = styled.div`
  font-size: 0.85rem;
  color: #c62828;
  margin-top: 0.35rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
`;

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 2px solid #4CAF50;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #2E7D32;
  font-weight: 600;
`;

const ErrorMessage = styled.div`
  background: rgba(255, 0, 0, 0.1);
  border: 2px solid #ff0000;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #cc0000;
  font-weight: 600;
`;

const ColorPickerContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ColorBox = styled.div<{ color: string | null }>`
  width: 1.2em;
  height: 1.2em;
  border-radius: 4px;
  border: 2px solid var(--card-border);
  background: ${props => props.color || '#e0e0e0'};
  cursor: pointer;
  transition: all 0.2s;
  display: inline-block;
  vertical-align: middle;
  position: relative;
  flex-shrink: 0;

  &:hover {
    border-color: var(--accent-orange);
    transform: scale(1.1);
  }

  &::after {
    content: ${props => props.color ? '""' : '"?"'};
    color: ${props => props.color ? 'transparent' : 'var(--text-gray)'};
    font-size: 0.7em;
    font-weight: 700;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const ColorPickerPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--bg-primary);
  border: 2px solid var(--card-border);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 280px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 0.3rem;
  margin-bottom: 0.75rem;
`;

const ColorOption = styled.button<{ color: string; selected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid ${props => props.selected ? 'var(--accent-orange)' : 'transparent'};
  background: ${props => props.color};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  ${props => props.selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 0.7rem;
      font-weight: 900;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }
  `}
`;

const ColorInput = styled.input`
  width: 100%;
  height: 40px;
  border: 2px solid var(--card-border);
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 0.75rem;
`;

const ColorPickerActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ColorPickerButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: var(--accent-orange);
    color: white;
    &:hover {
      background: var(--accent-orange-dark);
    }
  ` : `
    background: var(--bg-tertiary);
    color: var(--text-gray);
    &:hover {
      background: var(--card-border);
    }
  `}
`;

// Subscription Management Styles
const SubscriptionCard = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const AthleteSubscriptionItem = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubscriptionInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SubscriptionDetails = styled.div`
  flex: 1;
`;

const AthleteName = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-gray);
  margin-bottom: 0.5rem;
`;

const SubscriptionStatus = styled.div`
  font-size: 0.9rem;
  color: var(--text-muted);
`;

const SubscriptionActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PlanSelectGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  align-items: flex-end;
`;

const PlanSelectWrapper = styled.div`
  flex: 1;
`;

const WarningText = styled.div`
  font-size: 0.85rem;
  color: #ff9800;
  margin-top: 0.5rem;
  font-weight: 600;
`;

const AccountSettings: React.FC = () => {
  const { user, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'subscription'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    googleDriveFolderLink: user?.googleDriveFolderLink || '',
    club: user?.club || '',
    country: user?.country || '',
    dateOfBirth: user?.role === 'coach' ? (user?.dateOfBirth || '') : '',
    gender: user?.gender || '',
  });
  const [athleteId, setAthleteId] = useState<string | null>(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [athleteColor, setAthleteColor] = useState<string | null>(null);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [isSavingColor, setIsSavingColor] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  
  // Subscription management state
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [updatingAthleteId, setUpdatingAthleteId] = useState<string | null>(null);
  const [selectedPlans, setSelectedPlans] = useState<Record<string, { plan: string; billingPeriod: string }>>({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [showDeleteDataArea, setShowDeleteDataArea] = useState(false);
  const [isDeletingData, setIsDeletingData] = useState(false);
  const [deleteDataCategories, setDeleteDataCategories] = useState<Record<string, boolean>>({
    trainingSessions: false,
    equipment: false,
    bananaCup: false,
    athletes: false,
    weightHeight: false,
    profile: false,
  });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [dateOfBirthError, setDateOfBirthError] = useState<string | null>(null);

  const handleCopyAthleteId = async () => {
    const id = user?.athleteId;
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      setMessage({ type: 'error', text: 'Copy not supported' });
      setTimeout(() => setMessage(null), 2000);
    }
  };

  // Fetch athlete data for athletes
  useEffect(() => {
    const fetchAthleteData = async () => {
      if (user?.role === 'athlete') {
        try {
          const athlete = await athleteAPI.getMyAthlete();
          if (athlete) {
            setAthleteId(athlete._id);
            setAthleteColor(athlete.color || null);
            setSelectedColor(athlete.color || null);
            setProfileData(prev => ({
              ...prev,
              dateOfBirth: athlete.dateOfBirth || '',
            }));
          }
        } catch (error) {
          console.error('Error fetching athlete data:', error);
        }
      }
    };

    if (user?.role === 'athlete') {
      if (user?.athleteId) {
        setAthleteId(user.athleteId);
      }
      fetchAthleteData();
    }
  }, [user]);

  // Sync coach profile (dateOfBirth, gender) from user when not editing
  useEffect(() => {
    if (user?.role === 'coach' && !isEditing) {
      setProfileData(prev => ({
        ...prev,
        dateOfBirth: user.dateOfBirth || '',
        gender: user.gender || '',
      }));
    }
  }, [user?.role, user?.dateOfBirth, user?.gender, isEditing]);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const status = await stripeAPI.getSubscriptionStatus();
        setSubscriptionStatus(status);
        
        if (status.role === 'coach' && status.athletes) {
          // Fetch full athlete data for coaches
          const athleteData = await athleteAPI.getAll();
          setAthletes(athleteData);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    if (activeTab === 'subscription') {
      fetchSubscriptionStatus();
    }
  }, [activeTab, user]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setIsColorPickerOpen(false);
      }
    };

    if (isColorPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  // Well-organized color palette
  const colorPalette = [
    '#cc0000', '#ff0000', '#ff3333', '#ff6666', '#ff9999', '#ffcccc',
    '#cc0066', '#ff0066', '#ff3399', '#ff66cc', '#ff99ff', '#ffccff',
    '#cc6600', '#ff6600', '#ff9600', '#ff9900', '#ffcc00', '#ffcc33',
    '#ffff00', '#ffff33', '#ffff66', '#ffff99', '#ffffcc',
    '#00cc00', '#00ff00', '#33cc33', '#33ff33', '#66cc66', '#66ff66',
    '#00cc66', '#00ff66', '#33cc99', '#33ff99', '#66cccc', '#66ffcc',
    '#99cc99', '#99ff99', '#99ccff', '#99ffff', '#ccffcc', '#ccffff',
    '#0000cc', '#0000ff', '#0066cc', '#0066ff', '#3333cc', '#3333ff',
    '#3399cc', '#3399ff', '#6666cc', '#6666ff', '#66cccc', '#66ccff',
    '#9999cc', '#9999ff', '#99ccff', '#99ffff', '#ccccff', '#ccffff',
    '#6600cc', '#800080', '#9900cc', '#9900ff', '#9933cc', '#cc00cc',
    '#cc00ff', '#cc33cc', '#cc66cc', '#cc66ff', '#ff00ff', '#ff33ff',
    '#ff99ff', '#ffccff',
    '#000000', '#333333', '#666666', '#808080', '#999999', '#a0a0a0',
    '#c0c0c0', '#cccccc', '#e0e0e0', '#ffffff',
    '#654321', '#8b4513', '#8b6914', '#a0522d', '#a0826d', '#bc9a6a',
    '#cd853f', '#d2b48c', '#deb887', '#f4a460',
  ];

  const handleColorSave = async () => {
    if (!athleteId) return;
    
    try {
      setIsSavingColor(true);
      setMessage(null);
      
      const updatedAthlete = await athleteAPI.update(athleteId, { color: selectedColor });
      setAthleteColor(updatedAthlete.color || null);
      setMessage({ type: 'success', text: 'Color updated successfully!' });
      setIsColorPickerOpen(false);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to save color. Please try again.' });
      console.error('Error saving color:', err);
    } finally {
      setIsSavingColor(false);
    }
  };

  const handleColorCancel = () => {
    setSelectedColor(athleteColor);
    setIsColorPickerOpen(false);
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value,
    });
    if (name === 'dateOfBirth') setDateOfBirthError(null);
  };

  const validateDateOfBirth = (dob: string): string | null => {
    if (!dob || !dob.trim()) return null;
    const age = getAgeFromDateOfBirth(dob);
    if (age === null) return 'Please enter a valid date of birth.';
    if (age < MIN_AGE) return 'You must be at least 16 years old to use our software.';
    return null;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setDateOfBirthError(null);

    // Validate date of birth: must be at least 16 years old (athlete and coach)
    if ((user?.role === 'athlete' || user?.role === 'coach') && profileData.dateOfBirth && profileData.dateOfBirth.trim()) {
      const error = validateDateOfBirth(profileData.dateOfBirth);
      if (error) {
        setDateOfBirthError(error);
        setMessage({ type: 'error', text: error });
        return;
      }
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          googleDriveFolderLink: user?.role === 'coach' ? profileData.googleDriveFolderLink : undefined,
          club: profileData.club,
          country: profileData.country,
          ...(user?.role === 'coach' && {
            dateOfBirth: profileData.dateOfBirth || null,
            gender: profileData.gender && (profileData.gender === 'male' || profileData.gender === 'female') ? profileData.gender : null,
          }),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        if (user?.role === 'athlete' && athleteId && profileData.dateOfBirth) {
          try {
            await athleteAPI.update(athleteId, {
              dateOfBirth: profileData.dateOfBirth,
            });
            
            const updatedAthlete = await athleteAPI.getMyAthlete();
            setProfileData({
              firstName: profileData.firstName,
              lastName: profileData.lastName,
              email: user?.email || '',
              googleDriveFolderLink: result.user?.googleDriveFolderLink || '',
              club: result.user?.club || '',
              country: result.user?.country || '',
              dateOfBirth: updatedAthlete.dateOfBirth || profileData.dateOfBirth,
              gender: profileData.gender || '',
            });
          } catch (athleteError) {
            console.error('Error updating athlete dateOfBirth:', athleteError);
            setProfileData({
              firstName: profileData.firstName,
              lastName: profileData.lastName,
              email: user?.email || '',
              googleDriveFolderLink: result.user?.googleDriveFolderLink || '',
              club: result.user?.club || '',
              country: result.user?.country || '',
              dateOfBirth: profileData.dateOfBirth,
              gender: profileData.gender || '',
            });
          }
        } else {
          setProfileData({
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            email: user?.email || '',
            googleDriveFolderLink: result.user?.googleDriveFolderLink || '',
            club: result.user?.club || '',
            country: result.user?.country || '',
            dateOfBirth: user?.role === 'coach' ? (result.user?.dateOfBirth ?? profileData.dateOfBirth) : profileData.dateOfBirth,
            gender: user?.role === 'coach' ? (result.user?.gender ?? profileData.gender) : profileData.gender,
          });
        }

        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setIsEditing(false);
        refreshProfile();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while updating your profile' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setIsLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setShowPasswordForm(false);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to change password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while changing your password' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = async () => {
    setIsEditing(false);
    setShowPasswordForm(false);
    setShowDeleteDataArea(false);
    setDateOfBirthError(null);

    if (user?.role === 'athlete' && athleteId) {
      try {
        const athlete = await athleteAPI.getMyAthlete();
        setProfileData({
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          googleDriveFolderLink: user?.googleDriveFolderLink || '',
          club: user?.club || '',
          country: user?.country || '',
          dateOfBirth: athlete.dateOfBirth || '',
          gender: user?.gender || '',
        });
      } catch (error) {
        console.error('Error reloading athlete data:', error);
        setProfileData({
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          googleDriveFolderLink: user?.googleDriveFolderLink || '',
          club: user?.club || '',
          country: user?.country || '',
          dateOfBirth: '',
          gender: user?.gender || '',
        });
      }
    } else {
      setProfileData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        googleDriveFolderLink: user?.googleDriveFolderLink || '',
        club: user?.club || '',
        country: user?.country || '',
        dateOfBirth: user?.role === 'coach' ? (user?.dateOfBirth || '') : '',
        gender: user?.gender || '',
      });
    }
    
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setMessage(null);
  };

  const handleToggleDeleteDataCategory = (key: string) => {
    setDeleteDataCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRequestDeleteAllData = () => {
    setShowDeleteDataArea(true);
    setDeleteDataCategories({
      trainingSessions: false,
      equipment: false,
      bananaCup: false,
      athletes: false,
      weightHeight: false,
      profile: false,
    });
  };

  const handleDeleteSelectedClick = () => {
    const selected = Object.entries(deleteDataCategories)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (selected.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one category to delete.' });
      return;
    }
    if (selected.includes('profile')) {
      if (!window.confirm('Selecting "Profile data" will permanently close your account. You will be signed out. Continue?')) {
        return;
      }
    }
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDeleteSelected = async () => {
    const selected = Object.entries(deleteDataCategories)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (selected.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setIsDeletingData(true);
    setMessage(null);
    setShowDeleteConfirmModal(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/delete-all-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deleteCategories: selected }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage({ type: 'error', text: data.message || 'Failed to delete your data. Please try again.' });
        setIsDeletingData(false);
        return;
      }
      setMessage({ type: 'success', text: data.message || 'Selected data has been deleted.' });
      setShowDeleteDataArea(false);
      if (selected.includes('profile')) {
        logout();
        navigate('/', { replace: true });
      } else {
        refreshProfile();
      }
    } catch {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setIsDeletingData(false);
    }
  };

  // Subscription management handlers
  const handleCancelSubscription = async (athleteIdToCancel: string) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? It will remain active until the end of the billing period.')) {
      return;
    }

    try {
      setIsLoading(true);
      setMessage(null);
      
      await stripeAPI.cancelSubscription(athleteIdToCancel);
      setMessage({ type: 'success', text: 'Subscription will be canceled at the end of the billing period' });
      
      // Refresh subscription status
      const status = await stripeAPI.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to cancel subscription' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSubscription = async (athleteIdToUpdate: string, newPlan: string, billingPeriod: string) => {
    const athlete = athletes.find(a => a._id === athleteIdToUpdate) || 
                   (subscriptionStatus?.role === 'athlete' ? { subscription: subscriptionStatus.subscription } : null);
    
    if (!athlete) return;
    
    const currentPlan = athlete.subscription?.plan || 'free';
    const isDowngradePlan = isDowngrade(currentPlan, newPlan);
    
    if (isDowngradePlan) {
      if (!window.confirm(`Are you sure you want to downgrade from ${currentPlan.toUpperCase()} to ${newPlan.toUpperCase()}? No refunds will be provided.`)) {
        return;
      }
    }

    try {
      setUpdatingAthleteId(athleteIdToUpdate);
      setMessage(null);
      
      await stripeAPI.updateSubscription(athleteIdToUpdate, newPlan, billingPeriod);
      setMessage({ type: 'success', text: 'Subscription updated successfully!' });
      
      // Refresh subscription status
      const status = await stripeAPI.getSubscriptionStatus();
      setSubscriptionStatus(status);
      
      // Clear selected plan
      setSelectedPlans(prev => {
        const updated = { ...prev };
        delete updated[athleteIdToUpdate];
        return updated;
      });
    } catch (err: any) {
      if (err.response?.data?.redirectToCheckout) {
        navigate('/pricing');
      } else {
        setMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to update subscription' });
      }
    } finally {
      setUpdatingAthleteId(null);
    }
  };

  const getAthleteSubscription = (athleteId: string) => {
    if (subscriptionStatus?.role === 'athlete') {
      return subscriptionStatus.subscription;
    }
    if (subscriptionStatus?.role === 'coach' && subscriptionStatus.athletes) {
      return subscriptionStatus.athletes.find((a: any) => a.id === athleteId)?.subscription;
    }
    return null;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const renderProfileTab = () => {
    const dobDisplay = (() => {
      const dob = user?.role === 'athlete' ? profileData.dateOfBirth : user?.dateOfBirth;
      if (dob && dob !== '' && dob !== 'Not specified') {
        try {
          const date = new Date(dob);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
          }
          return dob;
        } catch {
          return dob;
        }
      }
      return '-';
    })();

    return (
      <>
        <SettingsSection>
          {!isEditing ? (
            <>
              <ProfileSectionHeader>
                <SectionTitle>Profile</SectionTitle>
                <ProfileHeaderActions>
<PrimaryButton
                  type="button"
                  onClick={() => {
                    if (user?.role === 'coach') {
                      setProfileData(prev => ({
                        ...prev,
                        dateOfBirth: user.dateOfBirth || '',
                        gender: user.gender || '',
                      }));
                    }
                    setIsEditing(true);
                  }}
                >
                  Edit Profile
                </PrimaryButton>
                </ProfileHeaderActions>
              </ProfileSectionHeader>

              <ProfileSegmentsRow>
              {/* Segment 1: Account & identity */}
              <ProfileCard>
                <ProfileCardTitle>Account &amp; identity</ProfileCardTitle>
                <ProfileCardsGrid>
                  <ProfileInfoTile>
                    <ProfileInfoTileLabel>First name</ProfileInfoTileLabel>
                    <ProfileInfoTileValue>{user?.firstName || '-'}</ProfileInfoTileValue>
                  </ProfileInfoTile>
                  <ProfileInfoTile>
                    <ProfileInfoTileLabel>Last name</ProfileInfoTileLabel>
                    <ProfileInfoTileValue>{user?.lastName || '-'}</ProfileInfoTileValue>
                  </ProfileInfoTile>
                  <ProfileInfoTile>
                    <ProfileInfoTileLabel>Email</ProfileInfoTileLabel>
                    <ProfileInfoTileValue>{user?.email || '-'}</ProfileInfoTileValue>
                  </ProfileInfoTile>
                  <ProfileInfoTile>
                    <ProfileInfoTileLabel>Role</ProfileInfoTileLabel>
                    <ProfileInfoTileValue>{user?.role === 'coach' ? 'Coach' : 'Athlete'}</ProfileInfoTileValue>
                  </ProfileInfoTile>
                  {user?.authProvider && (
                    <ProfileInfoTile>
                      <ProfileInfoTileLabel>Sign in</ProfileInfoTileLabel>
                      <ProfileInfoTileValue>
                        {user.authProvider === 'local' && 'Email & Password'}
                        {user.authProvider === 'google' && 'Google'}
                        {user.authProvider === 'both' && 'Email & Password + Google'}
                      </ProfileInfoTileValue>
                    </ProfileInfoTile>
                  )}
                </ProfileCardsGrid>
              </ProfileCard>

              {/* Segment 2: Profile details */}
              <ProfileCard>
                <ProfileCardTitle>Profile details</ProfileCardTitle>
                <ProfileCardsGrid>
                  <ProfileInfoTile>
                    <ProfileInfoTileLabel>Country</ProfileInfoTileLabel>
                    <ProfileInfoTileValue>{user?.country || '-'}</ProfileInfoTileValue>
                  </ProfileInfoTile>
                  <ProfileInfoTile>
                    <ProfileInfoTileLabel>Ski jumping club</ProfileInfoTileLabel>
                    <ProfileInfoTileValue>{user?.club || '-'}</ProfileInfoTileValue>
                  </ProfileInfoTile>
                  {user?.role === 'athlete' && (
                    <>
                      <ProfileInfoTile>
                        <ProfileInfoTileLabel>Gender</ProfileInfoTileLabel>
                        <ProfileInfoTileValue>
                          {user?.gender === 'male' ? 'Male' : user?.gender === 'female' ? 'Female' : '-'}
                        </ProfileInfoTileValue>
                      </ProfileInfoTile>
                      <ProfileInfoTile>
                        <ProfileInfoTileLabel>Date of birth</ProfileInfoTileLabel>
                        <ProfileInfoTileValue>{dobDisplay}</ProfileInfoTileValue>
                      </ProfileInfoTile>
                    </>
                  )}
                </ProfileCardsGrid>
              </ProfileCard>

              {/* Segment 3: Coach — Gender & Date of birth */}
              {user?.role === 'coach' && (
                <ProfileCard>
                  <ProfileCardTitle>Personal</ProfileCardTitle>
                  <ProfileCardsGrid>
                    <ProfileInfoTile>
                      <ProfileInfoTileLabel>Gender</ProfileInfoTileLabel>
                      <ProfileInfoTileValue>
                        {user?.gender === 'male' ? 'Male' : user?.gender === 'female' ? 'Female' : '-'}
                      </ProfileInfoTileValue>
                    </ProfileInfoTile>
                    <ProfileInfoTile>
                      <ProfileInfoTileLabel>Date of birth</ProfileInfoTileLabel>
                      <ProfileInfoTileValue>{dobDisplay}</ProfileInfoTileValue>
                    </ProfileInfoTile>
                  </ProfileCardsGrid>
                </ProfileCard>
              )}

              {user?.role === 'athlete' && (
                <ProfileCard>
                  <ProfileCardTitle>Athlete profile</ProfileCardTitle>
                  <ProfileCardsGrid>
                    <ProfileInfoTile>
                      <ProfileInfoTileLabel>Athlete ID</ProfileInfoTileLabel>
                      <ProfileInfoTileValue style={{
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        color: 'var(--accent-orange)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}>
                        {user?.athleteId || '-'}
                        {user?.athleteId && (
                          <>
                            <button
                              type="button"
                              onClick={handleCopyAthleteId}
                              title="Copy Athlete ID"
                              aria-label="Copy Athlete ID"
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: '0.25rem',
                                cursor: 'pointer',
                                color: 'var(--text-gray)',
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <CopyIcon size={18} />
                            </button>
                            {copySuccess && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--accent-orange)', fontWeight: 600 }}>
                                Copied!
                              </span>
                            )}
                          </>
                        )}
                      </ProfileInfoTileValue>
                    </ProfileInfoTile>
                    <ProfileInfoTile>
                      <ProfileInfoTileLabel>Athlete color</ProfileInfoTileLabel>
                      <ProfileInfoTileValue>
                        <ColorPickerContainer ref={colorPickerRef}>
                          <ColorBox
                            color={athleteColor || null}
                            onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                            title="Click to change athlete color"
                          />
                          {isColorPickerOpen && (
                            <ColorPickerPopover>
                              <ColorGrid>
                                {colorPalette.map((color) => (
                                  <ColorOption
                                    key={color}
                                    color={color}
                                    selected={selectedColor === color}
                                    onClick={() => setSelectedColor(color)}
                                    title={color}
                                  />
                                ))}
                              </ColorGrid>
                              <ColorInput
                                type="color"
                                value={selectedColor || '#e0e0e0'}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedColor(e.target.value)}
                                title="Custom color"
                              />
                              <ColorPickerActions>
                                <ColorPickerButton
                                  variant="secondary"
                                  onClick={handleColorCancel}
                                  disabled={isSavingColor}
                                >
                                  Cancel
                                </ColorPickerButton>
                                <ColorPickerButton
                                  variant="primary"
                                  onClick={handleColorSave}
                                  disabled={isSavingColor}
                                >
                                  {isSavingColor ? 'Saving...' : 'Save'}
                                </ColorPickerButton>
                              </ColorPickerActions>
                            </ColorPickerPopover>
                          )}
                        </ColorPickerContainer>
                      </ProfileInfoTileValue>
                    </ProfileInfoTile>
                  </ProfileCardsGrid>
                </ProfileCard>
              )}

              </ProfileSegmentsRow>
            </>
          ) : (
            <>
              <ProfileSectionHeader>
                <SectionTitle>Edit profile</SectionTitle>
                <Button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                  style={{
                    background: 'transparent',
                    color: 'var(--text-gray)',
                    border: '2px solid var(--card-border)',
                  }}
                >
                  Cancel
                </Button>
              </ProfileSectionHeader>
            <ProfileCard as="div">
            <form onSubmit={handleProfileUpdate}>
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleProfileChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleProfileChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  disabled
                />
                <InfoText>Email cannot be changed</InfoText>
              </FormGroup>
              <FormGroup>
                <Label>Role</Label>
                <Input
                  type="text"
                  value={user?.role === 'coach' ? 'Coach' : 'Athlete'}
                  disabled
                />
                <InfoText>Role cannot be changed</InfoText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="country">Country</Label>
                <Input
                  type="text"
                  id="country"
                  name="country"
                  value={profileData.country}
                  onChange={handleProfileChange}
                  placeholder="Enter your country"
                />
              </FormGroup>
              {user?.role === 'coach' && (
                <>
                  <FormGroup>
                    <Label htmlFor="gender">Gender</Label>
                    <Select
                      id="gender"
                      name="gender"
                      value={profileData.gender || ''}
                      onChange={handleProfileChange}
                    >
                      <option value="">—</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={profileData.dateOfBirth}
                      onChange={handleProfileChange}
                      onBlur={() => {
                        if (profileData.dateOfBirth?.trim()) {
                          setDateOfBirthError(validateDateOfBirth(profileData.dateOfBirth));
                        } else {
                          setDateOfBirthError(null);
                        }
                      }}
                      aria-invalid={!!dateOfBirthError}
                      aria-describedby={dateOfBirthError ? 'dateOfBirth-error' : undefined}
                    />
                    {dateOfBirthError && (
                      <InlineFieldError id="dateOfBirth-error" role="alert">
                        {dateOfBirthError}
                      </InlineFieldError>
                    )}
                  </FormGroup>
                </>
              )}
              {user?.role === 'athlete' && (
                <>
                  <FormGroup>
                    <Label>Athlete ID</Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Input
                        type="text"
                        value={user?.athleteId || '-'}
                        disabled
                        style={{ flex: 1 }}
                      />
                      {user?.athleteId && (
                        <>
                          <button
                            type="button"
                            onClick={handleCopyAthleteId}
                            title="Copy Athlete ID"
                            aria-label="Copy Athlete ID"
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '0.25rem',
                              cursor: 'pointer',
                              color: 'var(--text-gray)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              flexShrink: 0
                            }}
                          >
                            <CopyIcon size={18} />
                          </button>
                          {copySuccess && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--accent-orange)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              Copied!
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <InfoText>Athlete ID cannot be changed</InfoText>
                  </FormGroup>
                  <FormGroup>
                    <Label>Gender</Label>
                    <Input
                      type="text"
                      value={user?.gender === 'male' ? 'Male' : user?.gender === 'female' ? 'Female' : '-'}
                      disabled
                    />
                    <InfoText>Gender cannot be changed</InfoText>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={profileData.dateOfBirth}
                      onChange={handleProfileChange}
                      onBlur={() => {
                        if (profileData.dateOfBirth?.trim()) {
                          setDateOfBirthError(validateDateOfBirth(profileData.dateOfBirth));
                        } else {
                          setDateOfBirthError(null);
                        }
                      }}
                      aria-invalid={!!dateOfBirthError}
                      aria-describedby={dateOfBirthError ? 'dateOfBirth-error' : undefined}
                    />
                    {dateOfBirthError && (
                      <InlineFieldError id="dateOfBirth-error" role="alert">
                        {dateOfBirthError}
                      </InlineFieldError>
                    )}
                  </FormGroup>
                </>
              )}
              <FormGroup>
                <Label htmlFor="club">Ski Jumping Club</Label>
                <Input
                  type="text"
                  id="club"
                  name="club"
                  value={profileData.club}
                  onChange={handleProfileChange}
                  placeholder="Enter your ski jumping club"
                />
              </FormGroup>
              <ButtonGroup>
                <PrimaryButton type="submit" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Save Changes'}
                </PrimaryButton>
              </ButtonGroup>

              {/* Data & privacy — only visible in edit mode */}
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--card-border)' }}>
                <ProfileCardTitle style={{ marginBottom: '0.75rem' }}>Data &amp; privacy</ProfileCardTitle>
                <DangerButton type="button" onClick={handleRequestDeleteAllData} disabled={isDeletingData}>
                  Delete all my data
                </DangerButton>
                {showDeleteDataArea && (
                  <DeleteDataArea style={{ marginTop: '1rem' }}>
                    <DeleteDataTitle>Choose which data to delete (GDPR)</DeleteDataTitle>
                    <InfoText style={{ fontStyle: 'normal', marginBottom: '0.75rem' }}>
                      Select the categories you want to permanently delete. Your account stays active unless you select &quot;Profile data&quot;.
                    </InfoText>
                    <DeleteDataCheckboxRow>
                      <input
                        type="checkbox"
                        checked={deleteDataCategories.trainingSessions}
                        onChange={() => handleToggleDeleteDataCategory('trainingSessions')}
                      />
                      Training &amp; competition sessions
                    </DeleteDataCheckboxRow>
                    <DeleteDataCheckboxRow>
                      <input
                        type="checkbox"
                        checked={deleteDataCategories.equipment}
                        onChange={() => handleToggleDeleteDataCategory('equipment')}
                      />
                      Equipment
                    </DeleteDataCheckboxRow>
                    <DeleteDataCheckboxRow>
                      <input
                        type="checkbox"
                        checked={deleteDataCategories.bananaCup}
                        onChange={() => handleToggleDeleteDataCategory('bananaCup')}
                      />
                      Banana Cup leagues
                    </DeleteDataCheckboxRow>
                    <DeleteDataCheckboxRow>
                      <input
                        type="checkbox"
                        checked={deleteDataCategories.weightHeight}
                        onChange={() => handleToggleDeleteDataCategory('weightHeight')}
                      />
                      Weight and height data
                    </DeleteDataCheckboxRow>
                    {user?.role === 'coach' && (
                      <DeleteDataCheckboxRow>
                        <input
                          type="checkbox"
                          checked={deleteDataCategories.athletes}
                          onChange={() => handleToggleDeleteDataCategory('athletes')}
                        />
                        Athlete records (athletes I created)
                      </DeleteDataCheckboxRow>
                    )}
                    {user?.role === 'athlete' && (
                      <DeleteDataCheckboxRow>
                        <input
                          type="checkbox"
                          checked={deleteDataCategories.athletes}
                          onChange={() => handleToggleDeleteDataCategory('athletes')}
                        />
                        My athlete profile (link to this account)
                      </DeleteDataCheckboxRow>
                    )}
                    <DeleteDataCheckboxRow>
                      <input
                        type="checkbox"
                        checked={deleteDataCategories.profile}
                        onChange={() => handleToggleDeleteDataCategory('profile')}
                      />
                      Profile data (name, date of birth, etc.)
                    </DeleteDataCheckboxRow>
                    <DeleteDataWarning>Selecting &quot;Profile data&quot; will permanently close your account.</DeleteDataWarning>
                    <ButtonGroup style={{ marginTop: '1rem' }}>
                      <SecondaryButton type="button" onClick={() => setShowDeleteDataArea(false)} disabled={isDeletingData}>
                        Cancel
                      </SecondaryButton>
                      <DangerButton type="button" onClick={handleDeleteSelectedClick} disabled={isDeletingData}>
                        Delete selected
                      </DangerButton>
                    </ButtonGroup>
                  </DeleteDataArea>
                )}
              </div>

              {user?.authProvider !== 'google' && (
                <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--card-border)' }}>
                  {!showPasswordForm ? (
                    <Button
                      type="button"
                      onClick={() => setShowPasswordForm(true)}
                      style={{
                        background: 'transparent',
                        color: 'var(--accent-orange)',
                        border: '2px solid var(--accent-orange)',
                      }}
                    >
                      CHANGE PASSWORD
                    </Button>
                  ) : (
                  <form onSubmit={handlePasswordUpdate}>
                    <FormGroup>
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <Input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength={6}
                      />
                      <InfoText>Password must be at least 6 characters long</InfoText>
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="confirmPassword">Confirm New Password</Label>
                      <Input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength={6}
                      />
                    </FormGroup>
                    <ButtonGroup>
                      <PrimaryButton type="submit" disabled={isLoading}>
                        {isLoading ? 'Changing...' : 'Change Password'}
                      </PrimaryButton>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordData({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: '',
                          });
                        }}
                        disabled={isLoading}
                        style={{
                          background: 'transparent',
                          color: 'var(--text-gray)',
                          border: '2px solid var(--card-border)',
                        }}
                      >
                        Cancel
                      </Button>
                    </ButtonGroup>
                  </form>
                  )}
                </div>
              )}
            </form>
            </ProfileCard>
            </>
          )}
        </SettingsSection>
      </>
    );
  };

  const renderSubscriptionTab = () => {
    if (!subscriptionStatus) {
      return <div>Loading subscription information...</div>;
    }

    if (subscriptionStatus.role === 'athlete') {
      const subscription = subscriptionStatus.subscription;
      const plan = subscription?.plan || 'free';
      const billingPeriod = subscription?.billingPeriod || null;
      const status = subscription?.subscriptionStatus || 'free';
      const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd || false;
      const nextBillingDate = subscription?.nextBillingDate;

      return (
        <SettingsSection>
          <SectionTitle>My Subscription</SectionTitle>
          <SubscriptionCard>
            <SubscriptionInfo>
              <SubscriptionDetails>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <PlanBadge plan={plan as any} size="medium" />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                      {plan.toUpperCase()} Plan
                    </div>
                    {billingPeriod && (
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                        Billed {billingPeriod === 'monthly' ? 'monthly' : 'yearly'}
                      </div>
                    )}
                  </div>
                </div>
                <SubscriptionStatus>
                  Status: <strong>
                    {status === 'active' ? 'Active' : 
                     status === 'canceled' ? 'Canceled' : 
                     status === 'past_due' ? 'Past Due' : 
                     'Free'}
                  </strong>
                  {cancelAtPeriodEnd && status === 'active' && (
                    <div style={{ color: '#ff9800', marginTop: '0.5rem', fontWeight: 600 }}>
                      ⚠️ Will cancel at end of billing period
                    </div>
                  )}
                  {status === 'canceled' && (
                    <div style={{ color: '#f44336', marginTop: '0.5rem', fontWeight: 600 }}>
                      ⚠️ Subscription has been canceled
                    </div>
                  )}
                  {nextBillingDate && status === 'active' && !cancelAtPeriodEnd && (
                    <div style={{ marginTop: '0.5rem' }}>
                      Next billing: {formatDate(nextBillingDate)}
                    </div>
                  )}
                </SubscriptionStatus>
              </SubscriptionDetails>
            </SubscriptionInfo>

            {plan !== 'free' && status === 'active' && (
              <>
                {!cancelAtPeriodEnd ? (
                  <ButtonGroup>
                    <DangerButton 
                      onClick={() => handleCancelSubscription(athleteId || '')}
                      disabled={isLoading || !athleteId}
                    >
                      Cancel Subscription
                    </DangerButton>
                  </ButtonGroup>
                ) : (
                  <InfoText style={{ color: '#ff9800', fontWeight: 600 }}>
                    Subscription is scheduled to cancel at the end of the billing period.
                  </InfoText>
                )}
              </>
            )}

            {plan === 'free' && (
              <ButtonGroup>
                <PrimaryButton onClick={() => navigate('/pricing')}>
                  View Plans & Subscribe
                </PrimaryButton>
              </ButtonGroup>
            )}
          </SubscriptionCard>
        </SettingsSection>
      );
    } else {
      // Coach view - show all athletes' subscriptions
      return (
        <SettingsSection>
          <SectionTitle>Athlete Subscriptions</SectionTitle>
          <div style={{ marginTop: '1.25rem' }}>
          {subscriptionStatus.athletes && subscriptionStatus.athletes.length > 0 ? (
            subscriptionStatus.athletes.map((athleteSub: any) => {
              const athlete = athletes.find(a => a._id === athleteSub.id);
              const subscription = athleteSub.subscription;
              const plan = subscription?.plan || 'free';
              const billingPeriod = subscription?.billingPeriod || null;
              const status = subscription?.subscriptionStatus || 'free';
              const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd || false;
              const nextBillingDate = subscription?.nextBillingDate;
              const selectedPlan = selectedPlans[athleteSub.id] || { plan, billingPeriod: billingPeriod || 'monthly' };

              return (
                <AthleteSubscriptionItem key={athleteSub.id}>
                  <SubscriptionInfo>
                    <SubscriptionDetails>
                      <AthleteName>
                        {athleteSub.name}
                        <span style={{ marginLeft: '0.5rem' }}>
                          <PlanBadge plan={plan as any} size="small" />
                        </span>
                      </AthleteName>
                      <SubscriptionStatus>
                        Plan: <strong>{plan.toUpperCase()}</strong>
                        {billingPeriod && ` • Billed ${billingPeriod === 'monthly' ? 'monthly' : 'yearly'}`}
                        <br />
                        Status: <strong>
                          {status === 'active' ? 'Active' : 
                           status === 'canceled' ? 'Canceled' : 
                           status === 'past_due' ? 'Past Due' : 
                           'Free'}
                        </strong>
                        {cancelAtPeriodEnd && status === 'active' && (
                          <div style={{ color: '#ff9800', marginTop: '0.5rem', fontWeight: 600 }}>
                            ⚠️ Will cancel at end of billing period
                          </div>
                        )}
                        {status === 'canceled' && (
                          <div style={{ color: '#f44336', marginTop: '0.5rem', fontWeight: 600 }}>
                            ⚠️ Subscription has been canceled
                          </div>
                        )}
                        {nextBillingDate && status === 'active' && !cancelAtPeriodEnd && (
                          <div style={{ marginTop: '0.5rem' }}>
                            Next billing: {formatDate(nextBillingDate)}
                          </div>
                        )}
                      </SubscriptionStatus>
                    </SubscriptionDetails>
                  </SubscriptionInfo>

                  {plan !== 'free' && status === 'active' && (
                    <>
                      {!cancelAtPeriodEnd && (
                        <SubscriptionActions>
                          <DangerButton 
                            onClick={() => handleCancelSubscription(athleteSub.id)}
                            disabled={isLoading}
                            style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                          >
                            CANCEL SUBSCRIPTION
                          </DangerButton>
                        </SubscriptionActions>
                      )}
                    </>
                  )}

                  {plan === 'free' && (
                    <ButtonGroup>
                      <PrimaryButton 
                        onClick={() => navigate('/pricing')}
                        style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                      >
                        Subscribe
                      </PrimaryButton>
                    </ButtonGroup>
                  )}
                </AthleteSubscriptionItem>
              );
            })
          ) : (
            <SubscriptionCard>
              <InfoText>No athletes found. Add athletes to manage their subscriptions.</InfoText>
            </SubscriptionCard>
          )}
          </div>
        </SettingsSection>
      );
    }
  };

  return (
    <Container>
      <PageTitle>Account Settings</PageTitle>
      <PageSubtitle>
        Manage your account information, profile, and subscriptions.
      </PageSubtitle>

      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}

      <TabsContainer>
        <Tab active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
          Profile
        </Tab>
        <Tab active={activeTab === 'subscription'} onClick={() => setActiveTab('subscription')}>
          Subscriptions
        </Tab>
      </TabsContainer>

      {activeTab === 'profile' && renderProfileTab()}
      {activeTab === 'subscription' && renderSubscriptionTab()}

      {showDeleteConfirmModal && (
        <DeleteConfirmOverlay onClick={() => !isDeletingData && setShowDeleteConfirmModal(false)}>
          <DeleteConfirmModal onClick={(e) => e.stopPropagation()}>
            <DeleteConfirmTitle>Delete selected data?</DeleteConfirmTitle>
            <DeleteConfirmText>
              This will permanently delete the selected data.
              {deleteDataCategories.profile && ' Your account will be closed and you will be signed out.'}
            </DeleteConfirmText>
            <DeleteConfirmWarning>
              Warning: This action is permanent and cannot be undone. Deleted data cannot be recovered.
            </DeleteConfirmWarning>
            <DeleteConfirmButtons>
              <SecondaryButton type="button" onClick={() => !isDeletingData && setShowDeleteConfirmModal(false)} disabled={isDeletingData}>
                Cancel
              </SecondaryButton>
              <DangerButton type="button" onClick={handleConfirmDeleteSelected} disabled={isDeletingData}>
                {isDeletingData ? 'Deleting…' : 'Delete permanently'}
              </DangerButton>
            </DeleteConfirmButtons>
          </DeleteConfirmModal>
        </DeleteConfirmOverlay>
      )}
    </Container>
  );
};

export default AccountSettings;
